import { Component, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { concatMap, takeUntil, tap, zipAll } from 'rxjs/operators';
import { ModelStatus } from '../../../../shared/dtos/model-status';
import { ProgressData } from '../../../../shared/dtos/progress-data';
import { ResponseDto } from '../../../../shared/dtos/response.dto';
import { createSelectionItem, SelectionItem } from '../../../../shared/dtos/selection-item';
import { ResponseModel } from '../../../../shared/models/reponse.model';
import { ProgressIndicatingService } from '../../../../shared/services/progress-indicating.service';
import { ConfigurationOptions } from '../../dtos/confiuration-options.dto';
import { GeogatingMode, GeogatingType } from '../../dtos/geogating.enum';
import { UnsavedServiceLevelConfiguration } from '../../dtos/service-level-config.dto';
import { PartnerModel } from '../../models/partner.model';
import { UnsavedServiceLevelConfigurationGroup } from '../../models/unsaved-service-level-configuration-group.model';
import { TopconConfigurationService } from '../../services/topcon-configuration.service';
import { AbstractServiceLevelConfigDialogComponent } from './abstract-service-level-config-dialog.component';
import { ServiceLevelConfigDialogData } from './service-level-config-dialog.component';

export interface UnsavedServiceLevelConfigDialogData {
    partner: PartnerModel,
    configurationOptions: ConfigurationOptions
}

@Component({
    selector: 'unsaved-service-level-config-dialog',
    templateUrl: './unsaved-service-level-config-dialog.component.html',
    styleUrls: ['./service-level-config-dialog.component.scss']
})
export class UnsavedServiceLevelConfigDialogComponent extends AbstractServiceLevelConfigDialogComponent implements OnDestroy {
    private readonly data: ServiceLevelConfigDialogData;
    private serviceLevelNameAndIds: {[name: string]: number} = {};

    public unsavedServiceLevelConfigGroup: UnsavedServiceLevelConfigurationGroup;
    public partner: PartnerModel;
    public serviceLevelName: string = '';
    public geogatingModeOptions: string[] = Object.values(GeogatingMode);
    public geogatingTypeOptions: string[] = Object.values(GeogatingType);
    public serviceLevelOptions: string[] = [];

    private progress$ = new Subject<ProgressData>();
    private unsubscribe$ = new Subject<void>();

    public constructor(
        public ref: DynamicDialogRef,
        config: DynamicDialogConfig,
        private service: TopconConfigurationService,
        private progressService: ProgressIndicatingService,
        private messageService: MessageService) {
        super(config.data.configurationOptions);
        this.data = config.data;
        this.data.configurationOptions.serviceLevels.forEach(s => {
            this.serviceLevelNameAndIds[s.name] = s.id;
        });
        this.unsavedServiceLevelConfigGroup = new UnsavedServiceLevelConfigurationGroup();
        this.partner = this.data.partner;
        this.marketSectors.forEach(ms => {
            this.marketSectorProductCodeMap[ms.id] = {
                geogatingMode: '',
                geogatingType: '',
                productCode: ''
            };
        });
    }

    public ngOnDestroy(): void {
        this.progress$.next({ header: '', done: true });
        this.progress$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public getServiceLevelOptions(): void {
        this.serviceLevelOptions = this.data.configurationOptions.serviceLevels.map(sl => sl.name);
    }

    public onServiceLevelNameChanged(): void {
        const serviceLevelId = this.getServiceLevelId(this.serviceLevelName);
        this.unsavedServiceLevelConfigGroup.updateServiceLevel(this.serviceLevelName, serviceLevelId);
    }

    private getServiceLevelId(serviceLevelName: string): number {
        return this.serviceLevelNameAndIds[serviceLevelName] ?? 0;
    }

    public onSelectedMarketSectorIdsChanged(): void {
        const originals = this.unsavedServiceLevelConfigGroup.getNewConfigs().map(c => c.marketSectorId);
        const newIds = this.selectedMarketSectorIds.filter(i => !originals.includes(i));
        const deletedIds = originals.filter(i => !this.selectedMarketSectorIds.includes(i));

        newIds.forEach(id => {
            this.marketSectorProductCodeMap[id] = this.marketSectorProductCodeMap[id] ?? {};
            const unsaved: UnsavedServiceLevelConfiguration = {
                marketSectorId: id,
                coverageLevels: this.getSelectedCoverageLevelItems(),
                durations: this.getSelectedDurationItems(),
                organizationId: this.partner.organizationId,
                serviceLevelName: this.serviceLevelName,
                serviceLevelId: this.getServiceLevelId(this.serviceLevelName),
                modelStatus: ModelStatus.NEW,
                productCode: this.marketSectorProductCodeMap[id].productCode,
                geogatingMode: this.marketSectorProductCodeMap[id].geogatingMode,
                geogatingType: this.marketSectorProductCodeMap[id].geogatingType
            };
            this.unsavedServiceLevelConfigGroup.addNewConfig(unsaved);
        });

        deletedIds.forEach(id => {
            this.unsavedServiceLevelConfigGroup.removeUnsavedConfigs(id);
        });
    }

    public onSelectedDurationIdsChanged(): void {
        this.unsavedServiceLevelConfigGroup.updateConfigDurations(this.getSelectedDurationItems().map(i => i.id));
    }

    public onSelectedCoverageLevelIdsChanged(): void {
        this.unsavedServiceLevelConfigGroup.updateConfigCoverageLevels(this.getSelectedCoverageLevelItems().map(i => i.id));
    }

    private getSelectedCoverageLevelItems(): SelectionItem[] {
        return this.selectedCoverageLevelIds?.map(i => createSelectionItem(i, '', '')) ?? [];
    }

    private getSelectedDurationItems(): SelectionItem[] {
        return this.selectedDurationIds?.map(i => createSelectionItem(i, '', '')) ?? [];
    }

    public onCancelClicked(): void {
        this.ref.close(false);
    }

    public onProductCodeBlurred($event: number): void {
        const marketSectorId = $event;
        const config = this.unsavedServiceLevelConfigGroup.getNewConfigByMarketSector(marketSectorId);

        config.productCode = this.marketSectorProductCodeMap[marketSectorId].productCode;
    }

    public onGeogatingModeBlurred($event: number): void {
        const marketSectorId = $event;
        const config = this.unsavedServiceLevelConfigGroup.getNewConfigByMarketSector(marketSectorId);

        config.geogatingMode = this.marketSectorProductCodeMap[marketSectorId].geogatingMode;
    }

    public onGeogatingTypeBlurred($event: number): void {
        const marketSectorId = $event;
        const config = this.unsavedServiceLevelConfigGroup.getNewConfigByMarketSector(marketSectorId);

        config.geogatingType = this.marketSectorProductCodeMap[marketSectorId].geogatingType;
    }

    public override disableSaveButton(): boolean {
        return super.disableSaveButton() || this.hasInvalidServiceLevelName();
    }

    public onSaveClicked(): void {
        this.progressService.show({
            progress$: this.progress$,
            initial: {
                done: false,
                header: 'Saving service level config',
                details: 'Please wait...'
            }
        });

        this.handleSavingNewServiceLevelConfigs();
    }

    public hasInvalidServiceLevelName(): boolean {
        return !/^([A-Za-z0-9_]+)$/.test(this.serviceLevelName);
    }

    private handleSavingNewServiceLevelConfigs(): void {
        const configsToBeAdded = this.unsavedServiceLevelConfigGroup.getNewConfigs();
        /**
         * Backend doesn't handle multiple configs of a new service level
         * so we have to send one first to get service level id then to send the rest
         */
        const firstConfig = configsToBeAdded[0];
        const otherConfigs: UnsavedServiceLevelConfiguration[] = [];

        for (let i = 1; i < configsToBeAdded.length; i++) {
            const config = configsToBeAdded[i];
            otherConfigs.push(config.getConfigurationData());
        }

        if (otherConfigs.length >= 1) {
            this.service
                .addServiceLevelConfiguration(firstConfig.getConfigurationData())
                .pipe(
                    tap(responseDto => {
                        otherConfigs.forEach(config => {
                            config.serviceLevelId = responseDto.serviceLevelConfigModel.serviceLevelId;
                        });
                    }),
                    concatMap(() => otherConfigs.map(config => this.service.addServiceLevelConfiguration(config))),
                    zipAll(),
                    takeUntil(this.unsubscribe$)
                ).subscribe(...this.handleSavingSubscription());
        } else {
            this.service
                .addServiceLevelConfiguration(firstConfig.getConfigurationData())
                .pipe(
                    takeUntil(this.unsubscribe$)
                ).subscribe(...this.handleSavingSubscription());
        }
    }

    private handleSavingSubscription(): ((response: unknown) => void)[] {
        return [
            (): void => {
                this.sendProgressDoneSignal();
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'All configurations saved successfully.' });
                this.ref.close(true);
            },
            (response: unknown): void => {
                this.sendProgressDoneSignal();
                const res = response as { error: ResponseDto };
                this.messageService.add({ severity: 'warn', summary: 'Fail', detail: new ResponseModel(res.error).message });
            }
        ];
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }
}
